/**
 * Shipping zone / rate resolution for checkout and admin settings.
 *
 * When store `settings.shippingZones` is empty, callers fall back to
 * {@link FALLBACK_SHIPPING_RATES} so checkout remains usable.
 */

import type { ShippingRate, ShippingZone } from '../types/index.js';

/** Used only when the store has no configured shipping zones. */
export const FALLBACK_SHIPPING_RATES: ShippingRate[] = [
  {
    id: 'standard',
    name: 'Standard Shipping',
    price: 5.99,
    estimatedDays: '5-7 days',
  },
  {
    id: 'express',
    name: 'Express Shipping',
    price: 12.99,
    estimatedDays: '2-3 days',
  },
  {
    id: 'overnight',
    name: 'Overnight Shipping',
    price: 24.99,
    estimatedDays: '1 day',
  },
];

export interface ResolveShippingOptionsInput {
  zones: ShippingZone[] | undefined | null;
  countryCode?: string | null;
  subtotal: number;
}

/**
 * Pick the matching zone for a destination country.
 * Prefers an exact country match; otherwise the first zone.
 */
export function resolveShippingZone(
  zones: ShippingZone[] | undefined | null,
  countryCode?: string | null
): ShippingZone | null {
  if (!zones || zones.length === 0) {
    return null;
  }
  const normalized = (countryCode ?? 'US').trim().toUpperCase();
  const exact = zones.find((zone) =>
    zone.countries.some((code) => code.trim().toUpperCase() === normalized)
  );
  return exact ?? zones[0] ?? null;
}

/**
 * Whether a rate applies to the current subtotal (min/max order value gates).
 */
export function isShippingRateAvailable(rate: ShippingRate, subtotal: number): boolean {
  if (typeof rate.minOrderValue === 'number' && subtotal < rate.minOrderValue) {
    return false;
  }
  if (typeof rate.maxOrderValue === 'number' && subtotal > rate.maxOrderValue) {
    return false;
  }
  return true;
}

/**
 * Resolve checkout shipping options from store zones for a destination + cart.
 * Falls back to {@link FALLBACK_SHIPPING_RATES} when no zones are configured.
 */
export function resolveShippingOptions(input: ResolveShippingOptionsInput): {
  options: ShippingRate[];
  usedFallback: boolean;
  zone: ShippingZone | null;
} {
  const zone = resolveShippingZone(input.zones, input.countryCode);
  if (!zone || zone.rates.length === 0) {
    return {
      options: FALLBACK_SHIPPING_RATES.map((rate) => ({ ...rate })),
      usedFallback: true,
      zone: null,
    };
  }

  const available = zone.rates.filter((rate) =>
    isShippingRateAvailable(rate, input.subtotal)
  );

  if (available.length === 0) {
    // Zone exists but every rate is gated (e.g. free-only with low cart) —
    // expose ungated rates from the same zone, else fallback.
    const ungated = zone.rates.filter(
      (rate) =>
        typeof rate.minOrderValue !== 'number' && typeof rate.maxOrderValue !== 'number'
    );
    if (ungated.length > 0) {
      return { options: ungated.map((rate) => ({ ...rate })), usedFallback: false, zone };
    }
    return {
      options: FALLBACK_SHIPPING_RATES.map((rate) => ({ ...rate })),
      usedFallback: true,
      zone,
    };
  }

  return {
    options: available.map((rate) => ({ ...rate })),
    usedFallback: false,
    zone,
  };
}

/** Resolve the shipping price for a selected method id (defaults to first option). */
export function resolveSelectedShippingPrice(
  options: ShippingRate[],
  shippingMethodId?: string | null
): number {
  if (options.length === 0) {
    return 0;
  }
  if (shippingMethodId) {
    const match = options.find((option) => option.id === shippingMethodId);
    if (match) {
      return match.price;
    }
  }
  return options[0]?.price ?? 0;
}

/** Create a new shipping zone with a default standard rate. */
export function createEmptyShippingZone(name = 'New Zone'): ShippingZone {
  const idSuffix = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  return {
    id: `zone-${idSuffix}`,
    name,
    countries: ['US'],
    rates: [
      {
        id: `rate-${idSuffix}`,
        name: 'Standard Shipping',
        price: 5.99,
        estimatedDays: '5-7 days',
      },
    ],
  };
}

/** Deep-clone zones for editable form state. */
export function cloneShippingZones(zones: ShippingZone[]): ShippingZone[] {
  return zones.map((zone) => ({
    ...zone,
    countries: [...zone.countries],
    rates: zone.rates.map((rate) => ({ ...rate })),
  }));
}
